// SPDX-License-Identifier: MIT
// The params table and timing spec follow TimeSlice-Nodes by SYSTMS,
// https://github.com/systms-ai/TimeSlice-Nodes @ b4bd03287247aec00e972273bf01f908b0648540.
// Copyright (c) 2026 SYSTMS. MIT License; the full text is in apps/desktop/THIRD_PARTY_NOTICES.md.
/**
 * TimeSlice: the slit-scan nodes behind the Time slice, Time wave and Dual time slice apps. They
 * run in ComfyUI for export, and the Board previews them on the GPU.
 *
 * One params table drives the operation's defaults, the app manifests and the panel. One timing
 * spec, shared by the engine and the preview, says which source frame each slice shows, how long
 * a trimmed clip is and where its sound starts. `__tests__/timeslice.test.ts` holds both against
 * the node's own code at the pinned revision.
 */
import { z } from 'zod';
import type { NudgeVideoWorkflow } from './nudge';

export type TimeSliceNode = 'effect' | 'wave' | 'dual';

/** The ComfyUI class each node is. */
export const TIMESLICE_CLASS: Record<TimeSliceNode, string> = { effect: 'TimeSliceEffect', wave: 'TimeSliceWave', dual: 'TimeSliceDual' };
/** The node each clip app runs. */
export const TIMESLICE_NODE_BY_WORKFLOW: Record<NudgeVideoWorkflow, TimeSliceNode> = { 'time-slice': 'effect', 'time-wave': 'wave', 'time-slice-dual': 'dual' };

const CURVES = ['linear', 'ease_in', 'ease_out', 'ease_in_out', 'exponential'] as const;
const SPACINGS = ['linear', 'ease_in', 'ease_out', 'ease_in_out', 'random'] as const;
const DIRECTIONS = ['forward', 'reverse', 'center_out', 'edges_in'] as const;
const LOOP_MODES = ['loop', 'ping_pong', 'trim'] as const;
const PATTERNS = ['alternate', 'pairs', 'aba', 'aabb', 'abba'] as const;
export type TimeSliceCurve = (typeof CURVES)[number];
export type TimeSliceSpacing = (typeof SPACINGS)[number];
export type TimeSliceDirection = (typeof DIRECTIONS)[number];
export type TimeSliceLoopMode = (typeof LOOP_MODES)[number];
export type TimeSlicePattern = (typeof PATTERNS)[number];

/** How each option reads in the panel. */
export const TIMESLICE_OPTION_LABELS: Record<string, string> = {
  linear: 'Linear', ease_in: 'Ease in', ease_out: 'Ease out', ease_in_out: 'Ease in and out', exponential: 'Exponential', random: 'Random',
  forward: 'Forward', reverse: 'Reverse', center_out: 'Center out', edges_in: 'Edges in',
  loop: 'Loop', ping_pong: 'Ping-pong', trim: 'Trim',
  alternate: 'Alternate', pairs: 'Pairs', aba: 'A B A', aabb: 'A A B B', abba: 'A B B A',
};

/** A number the node takes: whole or not, its range and its step. */
interface TimeSliceRange { kind: 'int' | 'float'; min: number; max: number; step: number }
const whole = (min: number, max: number): TimeSliceRange => ({ kind: 'int', min, max, step: 1 });
const SLICES = whole(1, 2000);
const OFFSET = whole(-120, 120);
const PEAK = whole(1, 120);
const BLEND = whole(0, 400);
const SEED = whole(0, 0xffffffff);
const JITTER = whole(0, 60);
const FRAMES = whole(0, 100000);
const ANGLE: TimeSliceRange = { kind: 'float', min: -180, max: 180, step: 1 };
const SPEED: TimeSliceRange = { kind: 'float', min: 0, max: 50, step: 0.25 };
const COUNT: TimeSliceRange = { kind: 'float', min: 0.25, max: 20, step: 0.25 };
const MIX: TimeSliceRange = { kind: 'float', min: 0, max: 1, step: 0.05 };

interface TimeSliceParamBase {
  id: string;
  label: string;
  /** On the first screen; the rest fold into Settings. */
  hero: boolean;
}
export interface TimeSliceNumberParam extends TimeSliceParamBase, TimeSliceRange { default: number }
export interface TimeSliceBooleanParam extends TimeSliceParamBase { kind: 'boolean'; default: boolean }
export interface TimeSliceSelectParam extends TimeSliceParamBase { kind: 'select'; default: string; options: readonly string[] }
export type TimeSliceParam = TimeSliceNumberParam | TimeSliceBooleanParam | TimeSliceSelectParam;

const num = (id: string, label: string, range: TimeSliceRange, value: number, hero = false): TimeSliceNumberParam => ({ id, label, hero, ...range, default: value });
// A choice with no default starts on its first option, as ComfyUI does.
const choice = (id: string, label: string, options: readonly string[], hero = false): TimeSliceSelectParam => ({ id, label, hero, kind: 'select', options, default: options[0]! });
const flag = (id: string, label: string, value: boolean): TimeSliceBooleanParam => ({ id, label, hero: false, kind: 'boolean', default: value });

/** Every parameter of each node, in the node's own order, with Genie's labels and first-screen split. */
export const TIMESLICE_PARAMS: Record<TimeSliceNode, readonly TimeSliceParam[]> = {
  effect: [
    num('num_slices', 'Slices', SLICES, 20, true),
    num('offset', 'Frame offset', OFFSET, 1, true),
    num('angle', 'Angle', ANGLE, 0, true),
    choice('spacing', 'Slice spacing', SPACINGS),
    choice('direction', 'Direction', DIRECTIONS, true),
    choice('loop_mode', 'Loop mode', LOOP_MODES, true),
    num('blend_width', 'Blend width', BLEND, 0),
    num('seed', 'Seed', SEED, 0),
    choice('cascade_curve', 'Cascade curve', CURVES),
    num('slice_jitter', 'Slice jitter', JITTER, 0),
    flag('animate', 'Animate', false),
    num('num_slices_end', 'Slices at end', SLICES, 60),
    num('offset_end', 'Frame offset at end', OFFSET, 6),
    num('angle_end', 'Angle at end', ANGLE, 0),
    choice('temporal_curve', 'Animation curve', CURVES),
    num('frame_count', 'Animation length', FRAMES, 0),
  ],
  wave: [
    num('num_slices', 'Slices', SLICES, 30, true),
    num('max_offset', 'Peak offset', PEAK, 8, true),
    num('wave_speed', 'Wave speed', SPEED, 2, true),
    num('wave_count', 'Wave count', COUNT, 1, true),
    num('angle', 'Angle', ANGLE, 0, true),
    choice('loop_mode', 'Loop mode', LOOP_MODES, true),
    num('blend_width', 'Blend width', BLEND, 0),
    choice('spacing', 'Slice spacing', SPACINGS),
    num('seed', 'Seed', SEED, 0),
  ],
  dual: [
    num('num_slices', 'Slices', SLICES, 20, true),
    num('offset_a', 'Clip A offset', OFFSET, 1, true),
    num('offset_b', 'Clip B offset', OFFSET, 1, true),
    num('angle', 'Angle', ANGLE, 0),
    choice('spacing', 'Slice spacing', SPACINGS),
    choice('direction', 'Direction', DIRECTIONS),
    choice('pattern', 'Pattern', PATTERNS, true),
    choice('loop_mode', 'Loop mode', LOOP_MODES, true),
    num('blend_width', 'Blend width', BLEND, 0),
    num('mix', 'Mix', MIX, 0.5, true),
    num('seed', 'Seed', SEED, 0),
    choice('cascade_curve', 'Cascade curve', CURVES),
    num('slice_jitter', 'Slice jitter', JITTER, 0),
    flag('animate', 'Animate', false),
    num('num_slices_end', 'Slices at end', SLICES, 60),
    num('offset_a_end', 'Clip A offset at end', OFFSET, 6),
    num('offset_b_end', 'Clip B offset at end', OFFSET, 6),
    num('angle_end', 'Angle at end', ANGLE, 0),
    choice('temporal_curve', 'Animation curve', CURVES),
    num('frame_count', 'Animation length', FRAMES, 0),
  ],
};

/** Every TimeSlice parameter with a default, for the clip operation. Where the nodes differ, Time slice's wins; each app sets its own. */
export const TIMESLICE_DEFAULTS: Record<string, number | string | boolean> = Object.fromEntries(
  (['dual', 'wave', 'effect'] as const).flatMap((node) => TIMESLICE_PARAMS[node].map((p) => [p.id, p.default] as const)),
);

const number = (range: TimeSliceRange) => (range.kind === 'int' ? z.number().int().min(range.min).max(range.max) : z.number().min(range.min).max(range.max));

export const TimeSliceEffectParamsSchema = z.object({
  num_slices: number(SLICES), offset: number(OFFSET), angle: number(ANGLE), spacing: z.enum(SPACINGS), direction: z.enum(DIRECTIONS),
  loop_mode: z.enum(LOOP_MODES), blend_width: number(BLEND), seed: number(SEED), cascade_curve: z.enum(CURVES), slice_jitter: number(JITTER),
  animate: z.boolean(), num_slices_end: number(SLICES), offset_end: number(OFFSET), angle_end: number(ANGLE), temporal_curve: z.enum(CURVES),
  frame_count: number(FRAMES),
}).strict();
export const TimeSliceWaveParamsSchema = z.object({
  num_slices: number(SLICES), max_offset: number(PEAK), wave_speed: number(SPEED), wave_count: number(COUNT), angle: number(ANGLE),
  loop_mode: z.enum(LOOP_MODES), blend_width: number(BLEND), spacing: z.enum(SPACINGS), seed: number(SEED),
}).strict();
export const TimeSliceDualParamsSchema = z.object({
  num_slices: number(SLICES), offset_a: number(OFFSET), offset_b: number(OFFSET), angle: number(ANGLE), spacing: z.enum(SPACINGS),
  direction: z.enum(DIRECTIONS), pattern: z.enum(PATTERNS), loop_mode: z.enum(LOOP_MODES), blend_width: number(BLEND), mix: number(MIX),
  seed: number(SEED), cascade_curve: z.enum(CURVES), slice_jitter: number(JITTER), animate: z.boolean(), num_slices_end: number(SLICES),
  offset_a_end: number(OFFSET), offset_b_end: number(OFFSET), angle_end: number(ANGLE), temporal_curve: z.enum(CURVES), frame_count: number(FRAMES),
}).strict();
export type TimeSliceEffectParams = z.infer<typeof TimeSliceEffectParamsSchema>;
export type TimeSliceWaveParams = z.infer<typeof TimeSliceWaveParamsSchema>;
export type TimeSliceDualParams = z.infer<typeof TimeSliceDualParamsSchema>;

// ── Timing spec ──────────────────────────────────────────────────────────────────────────────
// Float64 arithmetic in the node's own order, so every value rounds the way numpy's does.

/** Round half to even, as numpy's round and Python's round() do; JavaScript's Math.round rounds half up. */
export function roundHalfEven(x: number): number {
  const floor = Math.floor(x);
  const rest = x - floor;
  const rounded = rest > 0.5 ? floor + 1 : rest < 0.5 ? floor : floor % 2 === 0 ? floor : floor + 1;
  return rounded + 0; // never −0
}

/** The node's easing curves, t in [0, 1]. */
export function timesliceEase(t: number, curve: string): number {
  if (curve === 'ease_in') return t * t;
  if (curve === 'ease_out') { const v = 1 - t; return 1 - v * v; }
  if (curve === 'ease_in_out') { if (t < 0.5) return 2 * t * t; const v = -2 * t + 2; return 1 - (v * v) / 2; }
  if (curve === 'exponential') return Math.expm1(3 * t) / Math.expm1(3);
  return t;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const floorMod = (a: number, n: number) => ((a % n) + n) % n;

/** numpy.random.RandomState(seed) for a whole-number seed (MT19937), with the legacy draws the nodes make. */
export class NumpyRandomState {
  private readonly words = new Uint32Array(624);
  private index = 624;

  constructor(seed: number) {
    const mt = this.words;
    mt[0] = seed >>> 0;
    for (let i = 1; i < 624; i++) {
      const prev = mt[i - 1]!;
      mt[i] = (Math.imul(1812433253, prev ^ (prev >>> 30)) + i) >>> 0;
    }
  }

  private twist(): void {
    const mt = this.words;
    for (let k = 0; k < 624; k++) {
      const y = (mt[k]! & 0x80000000) | (mt[(k + 1) % 624]! & 0x7fffffff);
      mt[k] = mt[(k + 397) % 624]! ^ (y >>> 1) ^ (y & 1 ? 0x9908b0df : 0);
    }
    this.index = 0;
  }

  /** The next raw 32-bit word. */
  nextUint32(): number {
    if (this.index >= 624) this.twist();
    let y = this.words[this.index++]!;
    y ^= y >>> 11;
    y ^= (y << 7) & 0x9d2c5680;
    y ^= (y << 15) & 0xefc60000;
    y ^= y >>> 18;
    return y >>> 0;
  }

  /** random_sample(): 53 random bits in [0, 1); uniform(0, 1) draws the same values. */
  random(): number {
    const a = this.nextUint32() >>> 5;
    const b = this.nextUint32() >>> 6;
    return (a * 67108864 + b) / 9007199254740992;
  }

  /** A whole number in [0, max] by masked rejection (numpy's random_interval). */
  interval(max: number): number {
    if (max === 0) return 0;
    let mask = max;
    mask |= mask >>> 1; mask |= mask >>> 2; mask |= mask >>> 4; mask |= mask >>> 8; mask |= mask >>> 16;
    for (;;) {
      const value = (this.nextUint32() & mask) >>> 0;
      if (value <= max) return value;
    }
  }

  /** randint(low, high, size) into int64, for ranges under 2^32 (the legacy masked path). */
  randint(low: number, high: number, count: number): number[] {
    return Array.from({ length: count }, () => low + this.interval(high - 1 - low));
  }

  /** shuffle(list), in place, as for a Python list. */
  shuffle<T>(list: T[]): void {
    for (let i = list.length - 1; i > 0; i--) {
      const j = this.interval(i);
      const kept = list[i]!;
      list[i] = list[j]!;
      list[j] = kept;
    }
  }
}

/** Each slice's frame offset, before the clip's minimum is taken away (`_slice_offsets`). */
export function sliceOffsets(slices: number, offset: number, direction: string, curve: string, jitter: number, seed: number): number[] {
  let base: number[];
  if (slices <= 1) base = new Array<number>(Math.max(slices, 1)).fill(0);
  else {
    base = Array.from({ length: slices }, (_, i) => {
      const u = i / (slices - 1);
      const a = direction === 'reverse' ? 1 - u : direction === 'center_out' ? Math.abs(2 * u - 1) : direction === 'edges_in' ? 1 - Math.abs(2 * u - 1) : u;
      return roundHalfEven(timesliceEase(a, curve) * (slices - 1) * offset);
    });
  }
  if (jitter > 0) {
    const jitters = new NumpyRandomState((seed + 1234) >>> 0).randint(-jitter, jitter + 1, base.length);
    base = base.map((value, i) => value + jitters[i]!);
  }
  return base;
}

/** The source frame a raw index lands on: wrapped (loop), bounced (ping-pong) or clamped (trim). */
export function resolveFrame(raw: number, total: number, loopMode: string): number {
  if (total <= 1) return 0;
  if (loopMode === 'loop') return floorMod(raw, total);
  if (loopMode === 'ping_pong') {
    const cycle = 2 * total - 2;
    const at = floorMod(raw, cycle);
    return at < total ? at : cycle - at;
  }
  return Math.max(0, Math.min(raw, total - 1));
}

/** Which clip each slice shows in Dual (0 = A, 1 = B), before the mix rebalances it. */
export function sourcePattern(slices: number, pattern: string): number[] {
  const cycle = pattern === 'pairs' ? [0, 0, 1, 1] : pattern === 'aba' ? [0, 1, 0] : pattern === 'aabb' ? [0, 0, 1, 1] : pattern === 'abba' ? [0, 1, 1, 0] : [0, 1];
  return Array.from({ length: slices }, (_, i) => cycle[i % cycle.length]!);
}

/** Dual's clip per slice after the mix: a seeded shuffle moves slices between A and B until B holds round(slices × mix). */
export function biasedSourceMap(slices: number, pattern: string, mix: number, seed: number): number[] {
  const map = sourcePattern(slices, pattern);
  if (mix === 0.5) return map;
  const bCount = map.reduce((sum, clip) => sum + clip, 0);
  const targetB = Math.max(0, Math.min(slices, roundHalfEven(slices * mix)));
  const random = new NumpyRandomState((seed + 9999) >>> 0);
  const flip = (from: number, count: number) => {
    const indices = map.flatMap((clip, i) => (clip === from ? [i] : []));
    random.shuffle(indices);
    for (const i of indices.slice(0, count)) map[i] = 1 - from;
  };
  if (bCount < targetB) flip(0, targetB - bCount);
  else if (bCount > targetB) flip(1, bCount - targetB);
  return map;
}

/** What one output frame shows: how it is sliced, and for each slice the source frame (and, in Dual, the clip). */
export interface TimeSliceFrame {
  slices: number;
  angle: number;
  source: number[];
  clip?: number[];
}

export interface TimeSlicePlan {
  node: TimeSliceNode;
  /** Frames the node returns: the clip's length, or less after a trim. */
  numOutput: number;
  /** The source frame the output's sound starts at, so it stays in step with the slices that do not move. */
  audioShift: number;
  frame(t: number): TimeSliceFrame;
}

/** The animation's value at output frame t: 0 at the start, 1 once `length` frames have passed. */
function animationAt(t: number, length: number, curve: string): number {
  return timesliceEase(Math.min(1, t / Math.max(1, length - 1)), curve);
}

function extent(rows: readonly (readonly number[])[]): { min: number; max: number } {
  let min = Infinity, max = -Infinity;
  for (const row of rows) for (const value of row) { if (value < min) min = value; if (value > max) max = value; }
  return { min, max };
}

/** Time slice over a clip of `frames` frames. Offsets are shared between frames that slice alike. */
export function planEffect(p: TimeSliceEffectParams, frames: number): TimeSlicePlan & { offsets: number[][] } {
  const length = Math.max(1, p.animate && p.frame_count > 0 ? p.frame_count : frames);
  const at = (t: number) => {
    if (!p.animate) return { slices: p.num_slices, offset: p.offset, angle: p.angle };
    const tau = animationAt(t, length, p.temporal_curve);
    return { slices: Math.max(1, roundHalfEven(lerp(p.num_slices, p.num_slices_end, tau))), offset: roundHalfEven(lerp(p.offset, p.offset_end, tau)), angle: lerp(p.angle, p.angle_end, tau) };
  };
  const cache = new Map<string, number[]>();
  const offsets = Array.from({ length: frames }, (_, t) => {
    const { slices, offset } = at(t);
    const key = `${slices}:${offset}`;
    let row = cache.get(key);
    if (!row) cache.set(key, (row = sliceOffsets(slices, offset, p.direction, p.cascade_curve, p.slice_jitter, p.seed)));
    return row;
  });
  const { min, max } = extent(offsets);
  const numOutput = p.loop_mode === 'trim' ? Math.max(1, frames - (max - min)) : frames;
  return {
    node: 'effect', numOutput, audioShift: 0 - min, offsets,
    frame(t) {
      const { slices, angle } = at(t);
      return { slices, angle, source: offsets[t]!.map((offset) => resolveFrame(t + offset - min, frames, p.loop_mode)) };
    },
  };
}

/** Time wave over a clip of `frames` frames. A trim pads both ends by the peak offset. */
export function planWave(p: TimeSliceWaveParams, frames: number): TimeSlicePlan {
  const trim = p.loop_mode === 'trim';
  const numOutput = trim ? Math.max(1, frames - 2 * p.max_offset) : frames;
  const pad = trim ? p.max_offset : 0;
  return {
    node: 'wave', numOutput, audioShift: pad,
    frame(t) {
      const phase = p.wave_speed * t / Math.max(1, numOutput);
      const source = Array.from({ length: p.num_slices }, (_, i) => {
        const offset = roundHalfEven(p.max_offset * Math.sin(2 * Math.PI * (p.wave_count * i / Math.max(1, p.num_slices) + phase)));
        return resolveFrame(t + pad + offset, frames, p.loop_mode);
      });
      return { slices: p.num_slices, angle: p.angle, source };
    },
  };
}

/** Dual time slice over clip A (`framesA`) and clip B (`framesB`); the output is as long as the shorter one, or less after a trim. */
export function planDual(p: TimeSliceDualParams, framesA: number, framesB: number): TimeSlicePlan & { offsets: [number[], number[]][]; maps: number[][] } {
  const shorter = Math.min(framesA, framesB);
  const length = Math.max(1, p.animate && p.frame_count > 0 ? p.frame_count : shorter);
  const at = (t: number) => {
    if (!p.animate) return { slices: p.num_slices, a: p.offset_a, b: p.offset_b, angle: p.angle };
    const tau = animationAt(t, length, p.temporal_curve);
    return {
      slices: Math.max(1, roundHalfEven(lerp(p.num_slices, p.num_slices_end, tau))),
      a: roundHalfEven(lerp(p.offset_a, p.offset_a_end, tau)), b: roundHalfEven(lerp(p.offset_b, p.offset_b_end, tau)),
      angle: lerp(p.angle, p.angle_end, tau),
    };
  };
  const offsets: [number[], number[]][] = [], maps: number[][] = [], chosen: number[][] = [];
  let aLo = 1e9, aHi = -1e9, bLo = 1e9, bHi = -1e9;
  for (let t = 0; t < shorter; t++) {
    const { slices, a, b } = at(t);
    const map = biasedSourceMap(slices, p.pattern, p.mix, p.seed);
    const offA = sliceOffsets(slices, a, p.direction, p.cascade_curve, p.slice_jitter, p.seed);
    // Clip B's jitter is seeded apart from A's.
    const offB = sliceOffsets(slices, b, p.direction, p.cascade_curve, p.slice_jitter, p.seed + 7);
    const row = map.map((clip, i) => (clip === 0 ? offA[i]! : offB[i]!));
    const onA = row.filter((_, i) => map[i] === 0), onB = row.filter((_, i) => map[i] === 1);
    // A clip with no slice in a frame counts as offset 0 there, as in the node.
    for (const offset of onA.length ? onA : [0]) { aLo = Math.min(aLo, offset); aHi = Math.max(aHi, offset); }
    for (const offset of onB.length ? onB : [0]) { bLo = Math.min(bLo, offset); bHi = Math.max(bHi, offset); }
    offsets.push([offA, offB]); maps.push(map); chosen.push(row);
  }
  const numOutput = p.loop_mode === 'trim' ? Math.min(Math.max(1, framesA - (aHi - aLo)), Math.max(1, framesB - (bHi - bLo))) : shorter;
  return {
    node: 'dual', numOutput, audioShift: 0 - aLo, offsets, maps,
    frame(t) {
      const { slices, angle } = at(t);
      const map = maps[t]!;
      const source = chosen[t]!.map((offset, i) => (map[i] === 0 ? resolveFrame(t + offset - aLo, framesA, p.loop_mode) : resolveFrame(t + offset - bLo, framesB, p.loop_mode)));
      return { slices, angle, source, clip: map };
    },
  };
}

/**
 * Working memory a TimeSlice run needs in ComfyUI (est.): the clip and the output as float32 RGB,
 * so twice the clip, and three times for Dual's two inputs. `frames` is the longer input's count.
 */
export function timesliceMemoryBytes(frames: number, width: number, height: number, node: TimeSliceNode): number {
  return frames * width * height * 3 * 4 * (node === 'dual' ? 3 : 2);
}
